import React, { useState, useEffect, useCallback, useRef, useMemo } from 'react';
import BuilderOrganizationControls from '../BuilderOrganizationControls';
import useMobileKeyboardOpen from '../hooks/useMobileKeyboardOpen';
import {
  Dialog,
  DialogContent,
  DialogActions,
  Button,
  Box,
  Typography,
  IconButton,
  TextField,
  useMediaQuery,
  Alert,
  CircularProgress,
  Snackbar,
  Autocomplete,
  Collapse,
  Menu,
  MenuItem,
  ListItemIcon,
  ListItemText,
} from '@mui/material';
import Tooltip from '../LibraryTooltip';
import { useTheme } from '@mui/material/styles';
import {
  CloseOutlined as CloseIcon,
  DeleteOutlined as DeleteIcon,
  MoreVertOutlined as MoreVertIcon,
  ExpandMoreOutlined as ExpandMoreIcon,
  AddOutlined as AddIcon,
  AutoAwesomeOutlined as AutoAwesomeIcon,
  PersonOutlined as PersonLevelIcon,
  AdminPanelSettingsOutlined as AdminLevelIcon
} from '@mui/icons-material';
import BasicInfoTab from '../../context-repository/tabs/BasicInfoTab';
import PermissionSettings from '../permissions/PermissionSettings';
import CollectionSelector from '../../prompt-builder/components/CollectionSelector';
import CollectionBuilderDialog from '../collection-builder/CollectionBuilderDialog';
import EditPreviewToggle from '../EditPreviewToggle';
import MarkdownPreviewEditor from '../MarkdownPreviewEditor';
import {
  borderedDescriptionShellSx,
  embeddedMarkdownTextFieldSx,
  instructionBodyTextSx,
  instructionPreviewMarkdownSx,
} from '../markdownBodySx';
import {
  PRIMARY_WORKSPACE_CONTENT_MAX_PX,
  promptBuilderShellPaperSx,
} from '../prompt-display/expandedPromptShellPaperSx';
import {
  getGoogleDriveStatus,
  startGoogleDriveConnect,
  disconnectGoogleDrive,
  fetchWebContext,
  generateContextMetadata,
  createCollection,
  listCompanyUsers,
  listTeams,
  listGroups
} from '../../../services/api';
import { useAuth } from '../../../contexts/AuthContext';
import { useSessionData } from '../../../contexts/SessionDataContext';
import {
  KNOWLEDGE_BUILDER_CATEGORY_OPTIONS,
} from '../../../constants/contextFolders';
import { primarySectionHeaderSx, secondarySectionHeaderSx } from '../../../designTokens';
import { applyContextSharingPreset } from '../../../utils/entityPermissions';
import {
  readImportFileText,
  stripImportFileExtension,
} from '../../../utils/projectFolderImport';

const CONTEXT_CATEGORY_LABEL_SX = {
  ...secondarySectionHeaderSx,
  color: 'text.primary',
  display: 'block',
  mb: 0.75,
  fontSize: '0.875rem',
  letterSpacing: 0,
};
const KNOWLEDGE_DETAILS_HEADER_SX = {
  ...primarySectionHeaderSx,
  fontSize: '1.125rem',
  fontWeight: 600,
  letterSpacing: 0,
  lineHeight: 1.4,
};
const CONTEXT_DESCRIPTION_HELPER = 'Describe what this knowledge is and how it can be used.';

const truncateImportLabel = (value, max = 72) => {
  const text = String(value || '').trim();
  if (!text) return '';
  return text.length > max ? `${text.slice(0, max - 1)}…` : text;
};

const appendKnowledgeImport = (existingContent, heading, importedContent) => {
  const current = String(existingContent || '').trimEnd();
  const imported = String(importedContent || '').trim();
  if (!imported) return current;
  const section = `${heading}\n\n${imported}`;
  return current ? `${current}\n\n---\n\n${section}` : section;
};

const VISIBILITY_LEVELS = ['private', 'team', 'organization', 'custom'];

const VISIBILITY_TOOLTIPS = {
  private: 'Only you can see and use this knowledge.',
  team: 'Share with your team only.',
  organization: 'Visible to everyone in your organization.',
  custom: 'Set granular access: choose specific people, teams, or groups.'
};

const ContextBuilderDialog = ({
  open,
  onClose,
  isEditing = false,
  editingContextId = null,
  initialContextData = null,
  onSave,
  onDelete = null,
  userProfile: externalUserProfile = null,
  isTemporary = false,
  secondaryAction = null,
  notice = null,
  closeOnSave = true
}) => {
  const { user } = useAuth();
  const { getProfile } = useSessionData();
  const [userProfile, setUserProfile] = useState(externalUserProfile);
  const [editingDescription, setEditingDescription] = useState(false);
  const [detailsExpanded, setDetailsExpanded] = useState(true);
  const [moreAnchor, setMoreAnchor] = useState(null);
  const [generatingMetadata, setGeneratingMetadata] = useState(false);
  const [showCollectionBuilder, setShowCollectionBuilder] = useState(false);
  const [collectionRefreshKey, setCollectionRefreshKey] = useState(0);
  const titleManuallyEditedRef = useRef(false);
  const autoGeneratedTitleRef = useRef('');
  const latestContentRef = useRef('');
  const latestGeneratedTitleRef = useRef('');
  const [contextData, setContextData] = useState({
    name: '',
    description: '',
    contextType: 'direct_input',
    fileUrl: '',
    webUrl: '',
    content: '',
    source: '',
    sourceFileName: '',
    sourceFileType: '',
    tags: [],
    category: 'General',
    collectionId: null,
    collectionName: null,
    permissions: {
      companyAccess: false,
      teamAccess: [],
      groupAccess: [],
      individualAccess: [],
      canEdit: [],
      canShare: [],
      canDelete: [],
      viewOnlyAccess: []
    }
  });
  const [contextPreview, setContextPreview] = useState('');
  const [isUploading, setIsUploading] = useState(false);
  const [importSnackbar, setImportSnackbar] = useState({ open: false, message: '', severity: 'success' });
  /** When context type is web: fetch remote page vs store URL text only (no network fetch). */
  const [webUrlImportMode, setWebUrlImportMode] = useState('fetch');
  const [saving, setSaving] = useState(false);
  const [accessList, setAccessList] = useState([]);
  const [googleDriveStatus, setGoogleDriveStatus] = useState({
    configured: false,
    connected: false,
    email: null,
    scopes: [],
    connectedAt: null,
    updatedAt: null
  });
  const [googleDriveLoading, setGoogleDriveLoading] = useState(false);
  const [googleDriveActionLoading, setGoogleDriveActionLoading] = useState(false);
  const openAddAccessRef = useRef(null);
  const initializedDraftKeyRef = useRef(null);
  const googleDrivePopupRef = useRef(null);
  const googleDrivePopupPollRef = useRef(null);
  const theme = useTheme();
  const isMobile = useMediaQuery(theme.breakpoints.down('sm'));
  const mobileKeyboardOpen = useMobileKeyboardOpen(open && isMobile);

  const permissionLevels = {
    interact: {
      label: 'Interact',
      icon: <PersonLevelIcon />,
      description: 'Can view and use the knowledge in capabilities',
      permissions: ['view', 'use']
    },
    owner: {
      label: 'Co-owner',
      icon: <AdminLevelIcon />,
      description: 'Can do everything including edit, delete, and manage permissions of the knowledge',
      permissions: ['view', 'use', 'edit', 'delete', 'manage-permissions']
    }
  };

  const visibilityIndex = useMemo(() => {
    const p = contextData.permissions || {};
    const noAccess = !p.companyAccess && (!p.teamAccess || p.teamAccess.length === 0) &&
      (!p.groupAccess || p.groupAccess.length === 0) && (!p.individualAccess || p.individualAccess.length === 0);
    if (noAccess) return 0;
    if (p.companyAccess) return 2;
    const userTeamId = userProfile?.teamId;
    const singleTeam = p.teamAccess && p.teamAccess.length === 1 && p.teamAccess[0] === userTeamId;
    const viewOnly = Array.isArray(p.viewOnlyAccess) ? p.viewOnlyAccess : [];
    const canEdit = Array.isArray(p.canEdit) ? p.canEdit : [];
    const canDelete = Array.isArray(p.canDelete) ? p.canDelete : [];
    const meaningful = (ids) =>
      singleTeam && userTeamId ? ids.filter((id) => id !== userTeamId) : ids;
    const teamHasCustomRoles =
      userTeamId &&
      (meaningful(canEdit).length > 0 ||
        meaningful(canDelete).length > 0 ||
        meaningful(viewOnly).length > 0);
    if (singleTeam && !teamHasCustomRoles) return 1;
    return 3;
  }, [contextData.permissions, userProfile?.teamId]);

  const currentVisibility = VISIBILITY_LEVELS[visibilityIndex];

  const handleVisibilityChange = (_, value) => {
    if (value == null) return;
    const level = (typeof value === 'string' && VISIBILITY_LEVELS.includes(value)) ? value : VISIBILITY_LEVELS[typeof value === 'number' ? value : Number(value)];
    setContextData(prev => ({
      ...prev,
      permissions: applyContextSharingPreset(level, {
        userTeamId: userProfile?.teamId || null,
        creatorId: user?.id,
        prevPermissions: prev.permissions || {}
      })
    }));
  };

  const openAddCustomAccess = () => {
    openAddAccessRef.current?.();
  };

  const rebuildAccessListFromPermissions = useCallback(async (perms) => {
    if (!user?.companyId) return;
    try {
      const newAccessList = [];
      const getRoleForEntity = (entityId) => {
        const canDelete = perms.canDelete?.includes(entityId) || false;
        if (canDelete) return 'owner';
        const hasAccess = perms.individualAccess?.includes(entityId) || perms.teamAccess?.includes(entityId) || perms.groupAccess?.includes(entityId);
        return hasAccess ? 'interact' : null;
      };
      if (perms.individualAccess && perms.individualAccess.length > 0) {
        try {
          const usersResponse = await listCompanyUsers(user.companyId);
          const allUsers = usersResponse.users || [];
          const creatorId = contextData?.userId || user?.id;
          perms.individualAccess.forEach(userId => {
            if (creatorId && userId === creatorId) return;
            const userObj = allUsers.find(u => u.id === userId);
            if (userObj) {
              newAccessList.push({
                id: userObj.id,
                type: 'user',
                name: userObj.name || `${userObj.firstName || ''} ${userObj.lastName || ''}`.trim() || userObj.email,
                email: userObj.email,
                role: getRoleForEntity(userId),
                description: userObj.role || 'USER'
              });
            }
          });
        } catch (error) {
          console.error('Failed to load users for access list:', error);
        }
      }
      if (perms.teamAccess && perms.teamAccess.length > 0) {
        try {
          const teamsResponse = await listTeams(user.companyId);
          const allTeams = teamsResponse.teams || [];
          perms.teamAccess.forEach(teamId => {
            const team = allTeams.find(t => t.id === teamId);
            if (team) {
              newAccessList.push({
                id: team.id,
                type: 'team',
                name: team.name,
                role: getRoleForEntity(teamId),
                description: team.description || '',
                memberCount: team.memberCount || 0
              });
            }
          });
        } catch (error) {
          console.error('Failed to load teams for access list:', error);
        }
      }
      if (perms.groupAccess && perms.groupAccess.length > 0) {
        try {
          const groupsResponse = await listGroups(user.companyId);
          const allGroups = groupsResponse.groups || [];
          perms.groupAccess.forEach(groupId => {
            const group = allGroups.find(g => g.id === groupId);
            if (group) {
              newAccessList.push({
                id: group.id,
                type: 'group',
                name: group.name,
                role: getRoleForEntity(groupId),
                description: group.description || '',
                memberCount: group.memberCount || 0
              });
            }
          });
        } catch (error) {
          console.error('Failed to load groups for access list:', error);
        }
      }
      setAccessList(newAccessList);
    } catch (error) {
      console.error('Error rebuilding access list from permissions:', error);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps -- user?.id, contextData?.userId intentionally omitted
  }, [user?.companyId]);

  useEffect(() => {
    if (contextData?.permissions && currentVisibility === 'custom' && contextData?.id && user?.companyId) {
      const perms = contextData.permissions;
      const hasIndividual = perms.individualAccess && perms.individualAccess.length > 0;
      const hasTeam = perms.teamAccess && perms.teamAccess.length > 0;
      const hasGroup = perms.groupAccess && perms.groupAccess.length > 0;
      if (hasIndividual || hasTeam || hasGroup) {
        rebuildAccessListFromPermissions(perms);
      }
    }
  }, [contextData?.permissions, contextData?.id, user?.companyId, currentVisibility, rebuildAccessListFromPermissions]);

  // Fetch user profile if not provided (session cache)
  useEffect(() => {
    if (open && !externalUserProfile && !userProfile) {
      getProfile({ force: false })
        .then((profileData) => {
          const normalized = profileData?.profile || profileData?.user || profileData;
          setUserProfile(normalized);
        })
        .catch((err) => {
          console.error('Error loading user profile:', err);
        });
    } else if (externalUserProfile) {
      setUserProfile(externalUserProfile);
    }
  }, [open, externalUserProfile, userProfile, getProfile]);

  const loadGoogleDriveStatus = useCallback(async () => {
    setGoogleDriveLoading(true);
    try {
      const result = await getGoogleDriveStatus();
      setGoogleDriveStatus(result?.googleDrive || {
        configured: false,
        connected: false,
        email: null,
        scopes: [],
        connectedAt: null,
        updatedAt: null
      });
    } catch (error) {
      console.error('Error loading Google Docs status:', error);
      setGoogleDriveStatus({
        configured: false,
        connected: false,
        email: null,
        scopes: [],
        connectedAt: null,
        updatedAt: null
      });
    } finally {
      setGoogleDriveLoading(false);
    }
  }, []);

  useEffect(() => {
    if (open) {
      loadGoogleDriveStatus();
    }
  }, [open, loadGoogleDriveStatus]);

  useEffect(() => {
    if (!open) return undefined;

    const params = new URLSearchParams(window.location.search);
    const googleDriveResult = params.get('googleDrive');
    if (!googleDriveResult) {
      return undefined;
    }

    if (googleDriveResult === 'connected') {
      loadGoogleDriveStatus();
    } else if (googleDriveResult === 'error') {
      setImportSnackbar({
        open: true,
        message: params.get('googleDriveMessage') || 'Google Docs connection failed.',
        severity: 'error',
      });
    }

    params.delete('googleDrive');
    params.delete('googleDriveMessage');
    const nextSearch = params.toString();
    const nextUrl = `${window.location.pathname}${nextSearch ? `?${nextSearch}` : ''}${window.location.hash || ''}`;
    window.history.replaceState({}, '', nextUrl);
    return undefined;
  }, [open, loadGoogleDriveStatus]);

  useEffect(() => () => {
    if (googleDrivePopupPollRef.current) {
      window.clearInterval(googleDrivePopupPollRef.current);
    }
  }, []);

  // Initialize the draft once per open cycle so nested dialogs do not reset in-progress edits.
  useEffect(() => {
    if (!open) {
      initializedDraftKeyRef.current = null;
      return;
    }

    const draftKey = isEditing
      ? `edit:${editingContextId || initialContextData?.id || 'unknown'}`
      : initialContextData
        ? `prefill:${initialContextData.id || initialContextData.name || 'draft'}`
        : 'new';

    if (initializedDraftKeyRef.current === draftKey) {
      return;
    }
    initializedDraftKeyRef.current = draftKey;
    setWebUrlImportMode('fetch');
    setEditingDescription(!isEditing);
    setDetailsExpanded(true);

    if (isEditing && initialContextData) {
      const normalized = {
        ...initialContextData,
        category: initialContextData.category || 'General',
        permissions: {
          companyAccess: initialContextData.permissions?.companyAccess ?? false,
          teamAccess: initialContextData.permissions?.teamAccess ?? [],
          groupAccess: initialContextData.permissions?.groupAccess ?? [],
          individualAccess: initialContextData.permissions?.individualAccess ?? [],
          canEdit: initialContextData.permissions?.canEdit ?? [],
          canShare: initialContextData.permissions?.canShare ?? [],
          canDelete: initialContextData.permissions?.canDelete ?? [],
          viewOnlyAccess: initialContextData.permissions?.viewOnlyAccess ?? []
        }
      };
      setContextData(normalized);
      titleManuallyEditedRef.current = true;
      autoGeneratedTitleRef.current = '';
      latestContentRef.current = normalized.content || '';
      latestGeneratedTitleRef.current = '';
      if (initialContextData.content) {
        setContextPreview(initialContextData.content);
      }
    } else if (open && !isEditing && initialContextData) {
      // Create flow but with prefilled data (e.g. converting temporary context from prompt modal)
      const effectiveType = initialContextData.contextType || 'direct_input';
      const normalized = {
        ...initialContextData,
        contextType: effectiveType,
        category: initialContextData.category || 'General',
        source: initialContextData.source || (initialContextData.content ? 'MANUAL_ENTRY' : ''),
        permissions: {
          companyAccess: initialContextData.permissions?.companyAccess ?? true,
          teamAccess: Array.isArray(initialContextData.permissions?.teamAccess) ? initialContextData.permissions.teamAccess : (userProfile?.teamId ? [userProfile.teamId] : []),
          groupAccess: initialContextData.permissions?.groupAccess ?? [],
          individualAccess: initialContextData.permissions?.individualAccess ?? [],
          canEdit: initialContextData.permissions?.canEdit ?? [],
          canShare: initialContextData.permissions?.canShare ?? [],
          canDelete: initialContextData.permissions?.canDelete ?? [],
          viewOnlyAccess: initialContextData.permissions?.viewOnlyAccess ?? []
        }
      };
      setContextData(normalized);
      titleManuallyEditedRef.current = Boolean(normalized.name);
      autoGeneratedTitleRef.current = '';
      latestContentRef.current = normalized.content || '';
      latestGeneratedTitleRef.current = '';
      const preview = initialContextData.content || '';
      if (preview) {
        setContextPreview(preview);
      } else {
        setContextPreview('');
      }
    } else if (!isEditing) {
      setContextData({
        name: '',
        description: '',
        contextType: 'direct_input',
        fileUrl: '',
        webUrl: '',
        content: '',
        source: '',
        sourceFileName: '',
        sourceFileType: '',
        tags: [],
        category: 'General',
        collectionId: null,
        collectionName: null,
        permissions: {
          companyAccess: true,
          teamAccess: userProfile?.teamId ? [userProfile.teamId] : [],
          groupAccess: [],
          individualAccess: [],
          canEdit: [],
          canShare: [],
          canDelete: [],
          viewOnlyAccess: []
        }
      });
      titleManuallyEditedRef.current = false;
      autoGeneratedTitleRef.current = '';
      latestContentRef.current = '';
      latestGeneratedTitleRef.current = '';
      setContextPreview('');
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps -- userProfile?.teamId intentionally omitted to avoid reset on profile load
  }, [open, isEditing, editingContextId, initialContextData]);

  const handleClose = () => {
    initializedDraftKeyRef.current = null;
    if (googleDrivePopupPollRef.current) {
      window.clearInterval(googleDrivePopupPollRef.current);
      googleDrivePopupPollRef.current = null;
    }
    setContextPreview('');
    setImportSnackbar({ open: false, message: '', severity: 'success' });
    setIsUploading(false);
    setSaving(false);
    setEditingDescription(false);
    setMoreAnchor(null);
    setShowCollectionBuilder(false);
    setWebUrlImportMode('fetch');
    onClose();
  };

  const handleConnectGoogleDrive = async () => {
    setGoogleDriveActionLoading(true);

    try {
      const redirectPath = `${window.location.pathname}${window.location.search}`;
      const result = await startGoogleDriveConnect({
        origin: window.location.origin,
        redirectPath
      });
      const authUrl = result?.authUrl;

      if (!authUrl) {
        throw new Error('Missing Google authorization URL');
      }

      const popup = window.open(
        authUrl,
        'google-drive-auth',
        'popup=yes,width=560,height=760,resizable=yes,scrollbars=yes'
      );

      if (!popup) {
        window.location.assign(authUrl);
        return;
      }

      googleDrivePopupRef.current = popup;

      const cleanup = () => {
        window.removeEventListener('message', onMessage);
        if (googleDrivePopupPollRef.current) {
          window.clearInterval(googleDrivePopupPollRef.current);
          googleDrivePopupPollRef.current = null;
        }
        if (googleDrivePopupRef.current === popup) {
          googleDrivePopupRef.current = null;
        }
      };

      const onMessage = (event) => {
        if (event.origin !== window.location.origin) return;
        if (event.data?.type !== 'google-drive-auth') return;

        cleanup();
        setGoogleDriveActionLoading(false);

        if (event.data?.status === 'connected') {
          loadGoogleDriveStatus();
          return;
        }

        setImportSnackbar({
          open: true,
          message: event.data?.message || 'Google Docs connection failed.',
          severity: 'error',
        });
      };

      window.addEventListener('message', onMessage);
      googleDrivePopupPollRef.current = window.setInterval(() => {
        if (popup.closed) {
          cleanup();
          setGoogleDriveActionLoading(false);
          loadGoogleDriveStatus();
        }
      }, 500);

      popup.focus();
    } catch (error) {
      console.error('Error connecting Google Docs:', error);
      setImportSnackbar({
        open: true,
        message: error?.message || 'Failed to connect Google Docs.',
        severity: 'error',
      });
      setGoogleDriveActionLoading(false);
    }
  };

  const handleDisconnectGoogleDrive = async () => {
    setGoogleDriveActionLoading(true);
    try {
      await disconnectGoogleDrive();
      await loadGoogleDriveStatus();
    } catch (error) {
      console.error('Error disconnecting Google Docs:', error);
      setImportSnackbar({
        open: true,
        message: error?.message || 'Failed to disconnect Google Docs.',
        severity: 'error',
      });
    } finally {
      setGoogleDriveActionLoading(false);
    }
  };

  const getDirectInputTitle = (value) => {
    const titleText = String(value || '').replace(/\s+/g, ' ').trim();
    if (!titleText) return '';
    return titleText.length > 80 ? `${titleText.slice(0, 77)}...` : titleText;
  };

  const isDirectInputDraft = (data) => (
    data?.contextType === 'direct_input' ||
    (
      data?.contextType === 'file_load' &&
      (!data.source || data.source === 'MANUAL_ENTRY') &&
      !data.sourceFileName
    )
  );

  const handleContextInputChange = (field, value) => {
    if (field === 'webUrl') {
      setContextData(prev => ({
        ...prev,
        webUrl: value,
        source: value ? 'WEB_URL' : prev.source,
        content: prev.contextType === 'web' ? '' : prev.content,
        sourceFileName: prev.contextType === 'web' ? '' : prev.sourceFileName,
        sourceFileType: prev.contextType === 'web' ? '' : prev.sourceFileType
      }));
      if (contextData.contextType === 'web') {
        setContextPreview('');
      }
      return;
    }

    setContextData(prev => ({ ...prev, [field]: value }));
  };

  const handleTitleChange = (value) => {
    titleManuallyEditedRef.current = true;
    autoGeneratedTitleRef.current = '';
    latestGeneratedTitleRef.current = '';
    handleContextInputChange('name', value);
  };

  const processKnowledgeFile = async (file) => {
    if (!file) return;
    setIsUploading(true);
    try {
      const extension = String(file.name || '').split('.').pop()?.toLowerCase() || '';
      const parsedText = await readImportFileText(file);
      const titleFromFile = stripImportFileExtension(file.name) || file.name;
      const combinedContent = appendKnowledgeImport(
        latestContentRef.current || contextData.content,
        `Attached file: ${file.name}`,
        parsedText
      );
      setContextData((prev) => {
        const next = {
          ...prev,
          contextType: 'direct_input',
          fileUrl: file.name,
          content: appendKnowledgeImport(prev.content, `Attached file: ${file.name}`, parsedText),
          source: 'UPLOADED_FILE',
          sourceFileName: file.name,
          sourceFileType: file.type || extension || 'text/plain',
        };
        if (!prev.name) {
          titleManuallyEditedRef.current = true;
          autoGeneratedTitleRef.current = '';
          next.name = titleFromFile;
        }
        return next;
      });
      latestContentRef.current = combinedContent;
      setContextPreview(combinedContent);
      setImportSnackbar({
        open: true,
        message: `Parsed “${file.name}”`,
        severity: 'success',
      });
    } catch (err) {
      const message = typeof err === 'string' ? err : (err?.message || 'Failed to parse document');
      setImportSnackbar({ open: true, message, severity: 'error' });
    } finally {
      setIsUploading(false);
    }
  };

  const handleFileUpload = async (event) => {
    const file = event.target.files?.[0];
    if (!file) return;
    await processKnowledgeFile(file);
    if (event.target) event.target.value = '';
  };

  const handleContentChange = (value) => {
    latestContentRef.current = value;
    setContextData(prev => {
      const next = { ...prev, content: value };
      const shouldAutoTitle =
        isDirectInputDraft(prev) &&
        !titleManuallyEditedRef.current &&
        (!prev.name || prev.name === autoGeneratedTitleRef.current);
      if (shouldAutoTitle) {
        const nextTitle = getDirectInputTitle(value);
        next.name = nextTitle;
        autoGeneratedTitleRef.current = nextTitle;
        latestGeneratedTitleRef.current = nextTitle;
      }
      return next;
    });
    setContextPreview(value);
  };

  const formatWebSourceUrlHeader = (url) => {
    const u = String(url || '').trim();
    return u ? `Source URL:\n${u}\n\n` : '';
  };

  const handleWebUrlImport = async (mode = 'fetch') => {
    const rawUrl = contextData.webUrl || '';
    if (!rawUrl.trim()) {
      setImportSnackbar({ open: true, message: 'Enter a web URL first.', severity: 'warning' });
      return { ok: false, error: 'Enter a web URL first.' };
    }

    setIsUploading(true);
    try {
      if (mode === 'url_only') {
        const resolvedUrl = rawUrl.trim();
        const bodyText = `Source URL: ${resolvedUrl}`;
        const combinedContent = appendKnowledgeImport(
          latestContentRef.current || contextData.content,
          'Attached URL',
          bodyText
        );
        setContextData((prev) => ({
          ...prev,
          contextType: 'direct_input',
          webUrl: resolvedUrl,
          content: appendKnowledgeImport(prev.content, 'Attached URL', bodyText),
          source: 'WEB_URL',
          sourceFileName: prev.sourceFileName || '',
          sourceFileType: 'text/plain'
        }));
        latestContentRef.current = combinedContent;
        setContextPreview(combinedContent);
        setImportSnackbar({
          open: true,
          message: `Added link: ${truncateImportLabel(resolvedUrl)}`,
          severity: 'success',
        });
        return {
          ok: true,
          fetchedText: combinedContent,
          resolvedUrl,
          title: '',
          fileType: 'text/plain'
        };
      }

      const result = await fetchWebContext(rawUrl);
      const fetchedText = result?.text || '';
      const resolvedUrl = result?.resolvedUrl || result?.url || rawUrl.trim();
      const title = result?.metadata?.title || '';
      const fileType = result?.fileType || 'text/html';
      const importedContent = `${formatWebSourceUrlHeader(resolvedUrl)}${fetchedText}`;
      const combinedContent = appendKnowledgeImport(
        latestContentRef.current || contextData.content,
        title ? `Attached URL: ${title}` : 'Attached URL',
        importedContent
      );

      setContextData((prev) => ({
        ...prev,
        contextType: 'direct_input',
        webUrl: resolvedUrl,
        content: appendKnowledgeImport(
          prev.content,
          title ? `Attached URL: ${title}` : 'Attached URL',
          importedContent
        ),
        source: 'WEB_URL',
        sourceFileName: title || prev.sourceFileName,
        sourceFileType: fileType
      }));
      if (!contextData.name.trim() && title) {
        setContextData((prev) => ({ ...prev, name: title }));
      }
      latestContentRef.current = combinedContent;
      setContextPreview(combinedContent);
      const pageLabel = truncateImportLabel(title, 48);
      setImportSnackbar({
        open: true,
        message: pageLabel
          ? `Fetched “${pageLabel}”`
          : `Fetched content from ${truncateImportLabel(resolvedUrl)}`,
        severity: 'success',
      });
      return {
        ok: true,
        fetchedText: combinedContent,
        resolvedUrl,
        title,
        fileType
      };
    } catch (err) {
      const message = typeof err === 'string' ? err : (err?.message || 'Failed to fetch web content');
      setImportSnackbar({ open: true, message, severity: 'error' });
      return { ok: false, error: message };
    } finally {
      setIsUploading(false);
    }
  };

  const handleGenerateMetadata = async () => {
    if (!contextData?.content?.trim() || generatingMetadata) return;
    setGeneratingMetadata(true);
    try {
      const result = await generateContextMetadata(contextData.content);
      const { title, description } = result?.metadata || {};
      if (title) {
        titleManuallyEditedRef.current = true;
        autoGeneratedTitleRef.current = '';
        handleContextInputChange('name', title);
      }
      if (description) handleContextInputChange('description', description);
    } catch (err) {
      setImportSnackbar({ open: true, message: err?.message || 'Failed to generate knowledge information', severity: 'error' });
    } finally {
      setGeneratingMetadata(false);
    }
  };

  const handlePermissionsChange = (newPermissions) => {
    handleContextInputChange('permissions', newPermissions);
  };

  const handleSave = async () => {
    if (!onSave) return;
    setSaving(true);
    try {
      let effectiveContextData = contextData;
      let effectivePreview = contextPreview;
      const latestContent = latestContentRef.current || contextData.content || effectivePreview;
      const directInputAutoTitle = isDirectInputDraft(effectiveContextData)
        ? getDirectInputTitle(latestContent)
        : '';
      if (isDirectInputDraft(effectiveContextData) && latestContent) {
        effectiveContextData = {
          ...effectiveContextData,
          content: latestContent,
        };
        effectivePreview = latestContent;
      }

      if (contextData.contextType === 'web' && (!contextData.content?.trim() || !contextPreview)) {
        const mode = webUrlImportMode === 'url_only' ? 'url_only' : 'fetch';
        const fetched = await handleWebUrlImport(mode);
        if (!fetched?.ok) {
          throw new Error(mode === 'url_only' ? 'Could not add URL as knowledge' : 'Web content fetch failed');
        }
        effectiveContextData = {
          ...contextData,
          webUrl: fetched.resolvedUrl,
          content: fetched.fetchedText,
          source: 'WEB_URL',
          sourceFileName: fetched.title || contextData.sourceFileName,
          sourceFileType: fetched.fileType || contextData.sourceFileType
        };
        effectivePreview = fetched.fetchedText;
      }

      const normalizedCategory = String(effectiveContextData.category || '').trim() || 'General';
      const normalizedName = (effectiveContextData.name || '').trim() || directInputAutoTitle;
      await onSave({
        contextData: {
          ...effectiveContextData,
          name: normalizedName,
          category: normalizedCategory
        },
        contextPreview: effectivePreview,
        isEditing,
        editingContextId
      });
      if (closeOnSave) {
        handleClose();
      }
    } catch (err) {
      console.error('Error saving context:', err);
      setImportSnackbar({ open: true, message: err?.message || 'Could not save knowledge. Please try again.', severity: 'error' });
    } finally {
      setSaving(false);
    }
  };

  const isFormValid = () => {
    const latestContent = latestContentRef.current || contextData.content || contextPreview;
    const effectiveName = (contextData.name || '').trim() || (
      isDirectInputDraft(contextData) ? getDirectInputTitle(latestContent) : ''
    );
    if (!effectiveName) return false;
    if (contextData.contextType === 'web') {
      return !!contextData.webUrl.trim();
    }
    if (!latestContent?.trim() && !contextPreview) return false;
    if (contextData.contextType === 'file_load' && (!contextPreview || isUploading)) return false;
    return true;
  };
  const latestContent = latestContentRef.current || contextData.content || contextPreview;
  const effectiveDisplayName = (contextData.name || '').trim() || (
    isDirectInputDraft(contextData)
      ? latestGeneratedTitleRef.current || getDirectInputTitle(latestContent)
      : ''
  );
  const missingTitleOrContent =
    !effectiveDisplayName || (!latestContent?.trim() && !contextPreview);

  const inputSize = 'small';

  return (
    <Dialog
      open={open}
      aria-labelledby="knowledge-builder-title"
      onClose={handleClose}
      maxWidth="xl"
      fullWidth
      fullScreen={isMobile}
      PaperProps={{
        sx: {
          ...promptBuilderShellPaperSx,
          borderRadius: 2,
          bgcolor: 'background.paper',
          '& .MuiOutlinedInput-root': { borderRadius: '8px' },
        },
      }}
    >
        <Box sx={{ flexShrink: 0, width: '100%', maxWidth: PRIMARY_WORKSPACE_CONTENT_MAX_PX, mx: 'auto', px: { xs: 2, sm: 2.5 }, pt: 2, pb: 1.5, boxSizing: 'border-box' }}>
          {notice}
          {/* Knowledge builder header */}
          <Box sx={{ display: mobileKeyboardOpen ? 'none' : 'flex', alignItems: 'center', gap: { xs: 0.5, sm: 1 }, flexWrap: { xs: 'nowrap', sm: 'wrap' } }}>
            <Typography id="knowledge-builder-title" variant="h6" component="h1" sx={{ fontSize: { xs: '1rem', sm: '1.5rem' }, fontWeight: 600, letterSpacing: 0, lineHeight: 1.25, minWidth: 0, flex: '1 1 auto', whiteSpace: 'nowrap', overflow: 'hidden', textOverflow: 'ellipsis' }}>
              {isEditing ? 'Edit Knowledge' : 'Knowledge Builder'}
            </Typography>
            <Box sx={{ display: 'flex', alignItems: 'center', gap: { xs: 0.25, sm: 0.5 }, minWidth: 0, flexShrink: 0, flexWrap: 'nowrap', ml: 'auto' }}>
              {!isTemporary && <BuilderOrganizationControls
                visibility={currentVisibility} visibilityOptions={VISIBILITY_LEVELS}
                iconOnlyOnMobile
                visibilityDescriptions={VISIBILITY_TOOLTIPS}
                onVisibilityChange={(value) => handleVisibilityChange(null, value)}
                onManagePermissions={openAddCustomAccess}
                projectName={contextData.collectionName || (contextData.collectionId ? 'Project selected' : '')}
                onCreateProject={() => setShowCollectionBuilder(true)}
                projectSelector={(<CollectionSelector
                    selectedCollectionId={contextData.collectionId}
                    onCollectionChange={(data) => {
                      handleContextInputChange('collectionId', data?.collectionId || null);
                      handleContextInputChange('collectionName', data?.collectionName || null);
                    }}
                    userProfile={userProfile}
                    disabled={false}
                    refreshKey={collectionRefreshKey}
                    onCreateCollection={() => setShowCollectionBuilder(true)}
                    compact
                    hideCreateButton
                    dropdownMinWidth={320}
                    size={inputSize}
                  />)}
              />}
              {(isEditing && onDelete) ? (
                <Tooltip title="More actions">
                  <IconButton size="small" aria-label="More knowledge actions" onClick={(event) => setMoreAnchor(event.currentTarget)}>
                    <MoreVertIcon />
                  </IconButton>
                </Tooltip>
              ) : null}
              <Tooltip title="Close">
                <IconButton onClick={handleClose} aria-label="Close create knowledge dialog" sx={{ width: { xs: 32, sm: 'auto' }, height: { xs: 32, sm: 'auto' } }}>
                  <CloseIcon />
                </IconButton>
              </Tooltip>
            </Box>
          </Box>
        </Box>
        <Menu anchorEl={moreAnchor} open={Boolean(moreAnchor)} onClose={() => setMoreAnchor(null)}>
          <MenuItem sx={{ color: 'error.main' }} onClick={() => { handleClose(); onDelete?.(); }}>
            <ListItemIcon><DeleteIcon fontSize="small" color="error" /></ListItemIcon>
            <ListItemText>Delete knowledge</ListItemText>
          </MenuItem>
        </Menu>
      <DialogContent sx={{ flex: 1, overflow: 'auto', display: 'flex', flexDirection: 'column', p: 0, minHeight: 0, scrollbarGutter: 'stable both-edges' }}>
        <Box sx={{ display: 'flex', flexDirection: 'column', gap: 1.5, width: '100%', maxWidth: PRIMARY_WORKSPACE_CONTENT_MAX_PX, mx: 'auto', px: { xs: 2, sm: 2.5 }, pt: 1, pb: 2, boxSizing: 'border-box' }}>
          <TextField fullWidth size="small" label="Name"
            value={contextData.name || effectiveDisplayName || ''}
            onChange={(event) => handleTitleChange(event.target.value)}
            placeholder="Name this knowledge" inputProps={{ 'aria-label': 'Knowledge name' }} />

          {/* Content block (context type, load, editor, preview) — natural height; dialog scrolls as it grows */}
          <Box sx={{ flexShrink: 0, display: 'flex', flexDirection: 'column' }}>
            <BasicInfoTab
              key={`${isEditing ? 'edit' : 'new'}:${editingContextId || 'draft'}`}
              initialViewMode={isEditing ? 'preview' : 'edit'}
              contextData={contextData}
              handleContextInputChange={handleContextInputChange}
              handleFileUpload={handleFileUpload}
              onFileDrop={processKnowledgeFile}
              handleContentChange={handleContentChange}
              handleFetchWebUrl={(mode) => handleWebUrlImport(mode || webUrlImportMode)}
              webUrlImportMode={webUrlImportMode}
              onWebUrlImportModeChange={setWebUrlImportMode}
              contextPreview={contextPreview}
              handleGenerateSummary={() => {}}
              isUploading={isUploading}
              googleDriveStatus={googleDriveStatus}
              googleDriveLoading={googleDriveLoading}
              googleDriveActionLoading={googleDriveActionLoading}
              handleConnectGoogleDrive={handleConnectGoogleDrive}
              handleDisconnectGoogleDrive={handleDisconnectGoogleDrive}
              userProfile={userProfile}
              inputSize={inputSize}
              topRowExtras={null}
            />
          </Box>

          {/* Knowledge details */}
          <Box sx={{ display: 'flex', flexDirection: 'column', gap: 1.5 }}>
            <Box sx={{ display: 'flex', alignItems: 'center', flexWrap: 'wrap', gap: 1, minHeight: 36 }}>
              <Box component="button" type="button" aria-expanded={detailsExpanded} aria-controls="knowledge-details-content"
                onClick={() => setDetailsExpanded((expanded) => !expanded)}
                sx={{ display: 'flex', alignItems: 'center', gap: 0.5, border: 0, p: 0, bgcolor: 'transparent', cursor: 'pointer', textAlign: 'left', color: 'text.primary' }}>
                <Typography component="span" sx={KNOWLEDGE_DETAILS_HEADER_SX}>Knowledge details</Typography>
                <ExpandMoreIcon sx={{ fontSize: 20, color: 'text.secondary', transform: detailsExpanded ? 'rotate(180deg)' : 'none' }} />
              </Box>
              {!isTemporary && (
                <Tooltip title="Generate a name and description from this knowledge">
                  <span style={{ marginLeft: 'auto' }}>
                    <Button
                      size="small"
                      variant="text"
                      startIcon={generatingMetadata ? <CircularProgress size={16} color="inherit" /> : <AutoAwesomeIcon sx={{ fontSize: 16 }} />}
                      onClick={() => { setDetailsExpanded(true); handleGenerateMetadata(); }}
                      disabled={!contextData?.content?.trim() || generatingMetadata}
                      sx={{ textTransform: 'none', fontSize: { xs: '0.75rem', sm: '0.8125rem' }, px: 1, minHeight: 32, whiteSpace: 'nowrap' }}
                    >
                      {generatingMetadata ? 'Generating...' : 'Generate title & details'}
                    </Button>
                  </span>
                </Tooltip>
              )}
            </Box>
            <Collapse in={detailsExpanded} id="knowledge-details-content">
            <Box sx={{ display: 'flex', flexDirection: 'column', gap: 1.5 }}>
            <Box>
              <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', mb: 0.75 }}>
              <Typography component="span" sx={{ ...CONTEXT_CATEGORY_LABEL_SX, mb: 0 }}>
                Description
              </Typography>
              <EditPreviewToggle aria-label="Description view" mode={editingDescription ? 'edit' : 'preview'}
                onModeChange={(mode) => setEditingDescription(mode === 'edit')} />
              </Box>
          <Box sx={{ ...borderedDescriptionShellSx, mb: 0, p: 1.5 }}>
            <MarkdownPreviewEditor
              value={contextData.description || ''}
              onChange={(text) => handleContextInputChange('description', text)}
              mode={editingDescription ? 'edit' : 'preview'}
              onModeChange={(mode) => setEditingDescription(mode === 'edit')}
              showToggle={false}
              compact
              embedded
              minRows={2}
              contentTextSx={instructionBodyTextSx}
              placeholder={CONTEXT_DESCRIPTION_HELPER}
              renderEditor={() => (
                <TextField
                  fullWidth
                  multiline
                  minRows={2}
                  variant="standard"
                  value={contextData.description || ''}
                  inputProps={{ 'aria-label': 'Knowledge description' }}
                  onChange={(e) => handleContextInputChange('description', e.target.value)}
                  placeholder={CONTEXT_DESCRIPTION_HELPER}
                  InputProps={{ disableUnderline: true }}
                  sx={embeddedMarkdownTextFieldSx}
                />
              )}
              previewSx={{ px: 0, py: 0, '& .markdown-body': instructionPreviewMarkdownSx }}
            />
          </Box>
            </Box>

            {!isTemporary && (
              <Box>
                <Typography component="span" sx={CONTEXT_CATEGORY_LABEL_SX}>
                  Category
                </Typography>
                <Autocomplete freeSolo forcePopupIcon fullWidth size="small"
                  options={KNOWLEDGE_BUILDER_CATEGORY_OPTIONS.filter((category) => category !== 'Other')}
                  value={contextData.category || ''}
                  onChange={(_, value) => handleContextInputChange('category', value || 'General')}
                  onInputChange={(_, value, reason) => { if (reason === 'input') handleContextInputChange('category', value); }}
                  renderInput={(params) => <TextField {...params} placeholder="Select or enter category" inputProps={{ ...params.inputProps, 'aria-label': 'Category' }} />}
                />
              </Box>
            )}
            </Box>
            </Collapse>
          </Box>

        </Box>

        {/* Hidden PermissionSettings for Custom dialog */}
        {!isTemporary && (
          <Box sx={{ position: 'absolute', left: -9999, width: 1, height: 1, overflow: 'hidden' }} aria-hidden="true">
            <PermissionSettings
              permissions={contextData.permissions}
              onPermissionsChange={handlePermissionsChange}
              userProfile={userProfile}
              isEditing={!!contextData.id}
              entityType="context"
              hideLegacyToggles={true}
              visibility={currentVisibility}
              openAddAccessRef={openAddAccessRef}
              permissionLevels={permissionLevels}
              accessList={accessList}
              onAccessListChange={setAccessList}
              creatorId={contextData.userId || user?.id}
              entityData={contextData.id ? contextData : null}
            />
          </Box>
        )}
      </DialogContent>

      <DialogActions sx={{ display: mobileKeyboardOpen ? 'none' : 'flex', flexShrink: 0, p: 0, borderTop: '1px solid', borderColor: 'divider' }}>
        <Box sx={{ width: '100%', maxWidth: PRIMARY_WORKSPACE_CONTENT_MAX_PX, mx: 'auto', px: { xs: 2, sm: 2.5 }, py: 1.25, boxSizing: 'border-box', display: 'flex', alignItems: 'center', gap: 1, flexWrap: 'wrap' }}>
        <Button onClick={handleClose}>Cancel</Button>
        {secondaryAction}
        <Box sx={{ flex: 1 }} />
        <Tooltip
          title={missingTitleOrContent ? 'Title and content required' : ''}
          placement="top"
          disableHoverListener={!missingTitleOrContent}
        >
          <span>
            <Button
              variant="contained"
              onClick={handleSave}
              disabled={saving || !isFormValid()}
              sx={{ minWidth: 120 }}
            >
              {saving ? 'Saving...' : (isEditing ? 'Save changes' : (isTemporary ? 'Add to session' : 'Save knowledge'))}
            </Button>
          </span>
        </Tooltip>
        </Box>
      </DialogActions>

      {/* Collection Builder Dialog */}
      {!isTemporary && (
        <CollectionBuilderDialog
          open={showCollectionBuilder}
          onClose={() => setShowCollectionBuilder(false)}
          isEditing={false}
          initialCollectionData={null}
          onSave={async (collectionDataToSave) => {
            try {
              const result = await createCollection(collectionDataToSave);
              const createdCollection = result?.collection || result?.data?.collection || result;
              if (createdCollection && createdCollection.id) {
                setCollectionRefreshKey(prev => prev + 1);
                handleContextInputChange('collectionId', createdCollection.id);
                handleContextInputChange('collectionName', createdCollection.name || createdCollection.title || '');
                setShowCollectionBuilder(false);
              }
            } catch (err) {
              console.error('Error creating collection:', err);
              throw err;
            }
          }}
          userProfile={userProfile}
          categories={[]}
        />
      )}

      <Snackbar
        open={importSnackbar.open}
        autoHideDuration={6000}
        onClose={() => setImportSnackbar((prev) => ({ ...prev, open: false }))}
        anchorOrigin={{ vertical: 'top', horizontal: 'center' }}
      >
        <Alert
          onClose={() => setImportSnackbar((prev) => ({ ...prev, open: false }))}
          severity={importSnackbar.severity}
          sx={{ width: '100%' }}
        >
          {importSnackbar.message}
        </Alert>
      </Snackbar>
    </Dialog>
  );
};

export default ContextBuilderDialog;
